import React from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import { useNavigate } from '../../../../../src/hooks/useI18nNavigate';
import { Avatar } from "@edusense/shared-ui-kit";
import { Button } from "@edusense/shared-ui-kit";
import { TimeDisplay } from "@edusense/shared-ui-kit";
// FSD 아키텍처: features 레이어에서 SocialPost import
import { SocialPost } from "@edusense/features-social-post";
import { Tweet } from '../types';
// 도메인별 클라이언트로 분리된 import
import { voteOnPoll } from '@edusense/tweet-client';

import { toast } from 'react-hot-toast';

const DEFAULT_PROFILE_IMAGE = '/images/default-avatar.png';

interface TweetListProps {
  tweets: Tweet[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onRefresh?: () => void;
  onComment?: (id: string) => void;
}

export const TweetList: React.FC<TweetListProps> = ({
  tweets,
  loading,
  error,
  hasMore,
  onLoadMore,
  onRefresh,
  onComment
}) => {
  // 네비게이션 훅
  const navigate = useNavigate();


  // 투표 핸들러
  const handlePollVote = async (tweetId: string, optionId: string) => {
    try {
      // voteOnPoll API 직접 호출
      const response = await voteOnPoll({
        id: tweetId,
        pollVoteRequest: {
          optionId
        }
      });

      toast.success('투표가 성공적으로 반영되었습니다!');

      // 필요한 경우 목록 새로고침
      if (onRefresh) {
        onRefresh();
      }

      return response.data;
    } catch (error) {
      console.error('투표 오류:', error);
      toast.error('투표 처리 중 오류가 발생했습니다');
      throw error;
    }
  };

  if (loading && tweets.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        {error}
      </div>
    );
  }

  if (tweets.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        게시물이 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
      {tweets.map(tweet => (
        <SocialPost
          key={tweet.id}
          id={String(tweet.id)}
          content={tweet.content}
          author={tweet.author}
          createdAt={tweet.createdAt}
          images={tweet.mediaUrls || []}
          likesCount={tweet.engagement.likeCount || 0}
          repostsCount={tweet.engagement.retweetCount || 0}
          repliesCount={tweet.engagement.commentCount || 0}
          viewsCount={tweet.engagement.viewCount || 0}
          isLiked={tweet.isLiked}
          isReposted={tweet.isRetweeted}
          isQuote={tweet.isQuoted}
          quotedContent={tweet.quotedContent}
          replyToUser={tweet.replyToUser}
          isRetweet={tweet.isRetweet}
          retweetAuthor={tweet.retweetedBy}
          onDeleteSuccess={onRefresh}
          showActions={true}
          displayStyle="default"
          path={`/tweet/${tweet.id}`}
          poll={tweet.poll}
          onPollVote={(optionId) => handlePollVote(String(tweet.id), optionId)}
          className="border-b-0"
          onComment={onComment ? () => onComment(String(tweet.id)) : undefined}
          onClick={() => navigate(`/post/${tweet.id}`)}
        />
      ))}

      {loading && (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      )}

      {hasMore && !loading && (
        <div className="flex justify-center py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
          >
            더 보기
          </Button>
        </div>
      )}
    </div>
  );
}; 